import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { rupiah, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import NumberInput from "@/components/ui/number-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Lock, Save, Wallet, Receipt } from "lucide-react";

const PRESETS = {
  Perencanaan: [["DP", 40], ["Termin 2", 40], ["Pelunasan", 20]],
  Produksi: [["DP", 30], ["Termin 2", 40], ["Termin 3", 25], ["Serah Terima", 5]],
  "Lunas Langsung": [["Pelunasan Penuh", 100]],
  "50-50": [["DP", 50], ["Pelunasan", 50]],
};

const round = (n) => Math.round(n || 0);

// kind="design" (default) manages the Design/Perencanaan payment plan (nilai_deal),
// kind="produksi" manages the fully independent Production payment plan
// (nilai_produksi) — same UI/validation, different lead fields and endpoint, so a
// client can have separate payment schemes & ledgers for each revenue stream.
export const PaymentTermins = ({ lead, readOnly, kind = "design" }) => {
  const qc = useQueryClient();
  const isProd = kind === "produksi";
  const nilai = (isProd ? lead?.nilai_produksi : lead?.nilai_deal) || 0;
  const savedType = isProd ? lead?.produksi_payment_type : lead?.payment_type;
  const savedRows = isProd ? lead?.produksi_termins : lead?.termins;
  const [type, setType] = useState(savedType || "");
  const [rows, setRows] = useState(savedRows || []);
  const [saving, setSaving] = useState(false);
  // Design plans lock along with the deal (lead.locked); Production has no such lock —
  // it can be actively billed even while Design is closed/locked, or never engaged at all.
  const locked = !isProd && lead?.locked;
  const active = isProd ? !!lead?.produksi_closing_date : lead?.status === "Closing";

  useEffect(() => { setType(savedType || ""); setRows(savedRows || []); }, [lead?.lead_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (preset) => {
    setType(preset);
    setRows((PRESETS[preset] || []).map(([label, pct]) => ({ label, pct, amount: round(nilai * pct / 100) })));
  };
  const update = (i, field, val) => {
    setRows((rs) => rs.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, [field]: val };
      if (field === "pct") next.amount = round(nilai * (Number(val) || 0) / 100);
      return next;
    }));
    if (type !== "Custom") setType("Custom");
  };
  const addRow = () => { setRows((rs) => [...rs, { label: `Termin ${rs.length + 1}`, pct: 0, amount: 0, due_date: "" }]); setType("Custom"); };
  const removeRow = (i) => { setRows((rs) => rs.filter((_, idx) => idx !== i)); setType("Custom"); };

  const totalRencana = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const totalPct = rows.reduce((a, r) => a + (Number(r.pct) || 0), 0);
  const rencana = totalRencana > 0 ? totalRencana : nilai;
  const dibayar = (isProd ? lead?.produksi_paid_total : lead?.paid_total) || 0;
  const sisa = Math.max(0, (active ? nilai : rencana) - dibayar);
  const history = (isProd ? lead?.produksi_payment_history : lead?.payment_history) || [];

  const save = async () => {
    if (rows.length > 0 && Math.abs(totalPct - 100) > 0.5) {
      toast.error(`Total persentase termin harus 100% (saat ini ${totalPct}%). Sesuaikan dulu sebelum simpan.`);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/leads/${lead.lead_id}/${isProd ? "produksi-termins" : "termins"}`, {
        payment_type: type || "Custom",
        termins: rows.map((r) => ({ label: r.label || "", pct: Number(r.pct) || 0, amount: Number(r.amount) || 0, due_date: r.due_date || null })),
      });
      toast.success("Skema pembayaran disimpan");
      qc.invalidateQueries({ queryKey: ["lead", lead.lead_id] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["receivables"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid={isProd ? "payment-termins-produksi" : "payment-termins"}>
      <div className="grid grid-cols-3 gap-2 text-center mb-3">
        <div className="p-3 rounded-xl bg-[#18181B]"><div className="text-xs text-zinc-500">Rencana</div><div className="font-semibold text-blue-300 text-sm whitespace-nowrap tabular-nums">{rupiah(rencana)}</div></div>
        <div className="p-3 rounded-xl bg-[#18181B]"><div className="text-xs text-zinc-500">Dibayar</div><div className="font-semibold text-emerald-400 text-sm whitespace-nowrap tabular-nums">{rupiah(dibayar)}</div></div>
        <div className="p-3 rounded-xl bg-[#18181B]"><div className="text-xs text-zinc-500">Sisa</div><div className="font-semibold text-amber-400 text-sm whitespace-nowrap tabular-nums">{rupiah(sisa)}</div></div>
      </div>

      <div className="text-[11px] text-zinc-500 mb-2 flex items-start gap-1"><Receipt size={13} className="mt-0.5 shrink-0" />Ini hanya skema/rencana pembayaran. Pencatatan pembayaran masuk &amp; bukti transfer dilakukan di halaman Piutang.</div>

      {nilai <= 0 && <div className="text-xs text-amber-400 mb-3 flex items-center gap-1"><Wallet size={12} />{isProd ? "Isi Nilai Produksi dulu agar nominal termin terhitung otomatis." : "Isi Nilai Deal dulu agar nominal termin terhitung otomatis."}</div>}

      {!readOnly && !locked && (
        <div className="mb-3">
          <label className="text-[11px] text-zinc-500 mb-1 block">Skema Pembayaran (bisa dinego / diedit)</label>
          <Select value={PRESETS[type] ? type : ""} onValueChange={applyPreset}>
            <SelectTrigger data-testid={isProd ? "termin-preset-produksi" : "termin-preset"} className="bg-[#18181B] border-white/10 h-9"><SelectValue placeholder={type === "Custom" ? "Custom (diedit manual)" : "Pilih skema…"} /></SelectTrigger>
            <SelectContent className="bg-[#18181B] border-white/10">
              <SelectItem value="Perencanaan">Perencanaan (40 · 40 · 20)</SelectItem>
              <SelectItem value="Produksi">Produksi (30 · 40 · 25 · 5)</SelectItem>
              <SelectItem value="50-50">50 · 50</SelectItem>
              <SelectItem value="Lunas Langsung">Lunas Langsung (100%)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="p-2.5 rounded-xl bg-[#18181B]" data-testid={`${isProd ? "termin-row-produksi" : "termin-row"}-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <Input disabled={readOnly || locked} value={r.label} onChange={(e) => update(i, "label", e.target.value)} placeholder="Nama termin" className="bg-[#0f0f12] border-white/10 h-8 text-sm disabled:opacity-90 disabled:text-zinc-200 flex-1" data-testid={`${isProd ? "termin-label-produksi" : "termin-label"}-${i}`} />
              {!readOnly && !locked && <button onClick={() => removeRow(i)} className="text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg shrink-0"><Trash2 size={14} /></button>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-[10px] text-zinc-500">%</label><Input disabled={readOnly || locked} type="number" value={r.pct} onChange={(e) => update(i, "pct", e.target.value)} className="bg-[#0f0f12] border-white/10 h-8 text-sm disabled:opacity-90 disabled:text-zinc-200" data-testid={`${isProd ? "termin-pct-produksi" : "termin-pct"}-${i}`} /></div>
              <div><label className="text-[10px] text-zinc-500">Nominal</label><NumberInput disabled={readOnly || locked} value={r.amount} onChange={(e) => update(i, "amount", e.target.value)} className="bg-[#0f0f12] border-white/10 h-8 text-sm disabled:opacity-90 disabled:text-zinc-200" data-testid={`${isProd ? "termin-amount-produksi" : "termin-amount"}-${i}`} /></div>
              <div><label className="text-[10px] text-zinc-500">Jatuh Tempo</label><Input disabled={readOnly || locked} type="date" value={r.due_date || ""} onChange={(e) => update(i, "due_date", e.target.value)} className="bg-[#0f0f12] border-white/10 h-8 text-sm disabled:opacity-90 disabled:text-zinc-200" data-testid={`${isProd ? "termin-due-produksi" : "termin-due"}-${i}`} /></div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-zinc-600 text-center py-3">Belum ada skema. Pilih skema di atas atau tambah manual.</p>}
      </div>

      {!readOnly && !locked && (
        <div className="flex gap-2 mt-3">
          <Button data-testid={isProd ? "termin-add-produksi" : "termin-add"} variant="outline" onClick={addRow} className="h-9 bg-[#18181B] border-white/10 flex-1"><Plus size={14} className="mr-1.5" />Tambah Termin</Button>
          <Button data-testid={isProd ? "termin-save-produksi" : "termin-save"} onClick={save} disabled={saving || (rows.length > 0 && Math.abs(totalPct - 100) > 0.5)} className="h-9 bg-blue-600 hover:bg-blue-700 flex-1"><Save size={14} className="mr-1.5" />Simpan Skema</Button>
        </div>
      )}
      {locked && <div className="text-xs text-amber-400 flex items-center justify-center gap-1 py-2"><Lock size={12} />Skema terkunci. Buka kunci untuk mengubah.</div>}
      {rows.length > 0 && <div className={`text-[11px] mt-2 ${Math.abs(totalPct - 100) > 0.5 ? "text-red-400" : "text-zinc-500"}`}>Total persentase: {totalPct}%{Math.abs(totalPct - 100) > 0.5 ? " — harus 100% agar bisa disimpan" : " ✓"}</div>}

      {history.length > 0 && (
        <div className="mt-4 border-t border-white/5 pt-3">
          <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-2">Riwayat Pembayaran (baca-saja)</div>
          <div className="space-y-1.5">
            {history.map((p, i) => (
              <div key={i} className="flex justify-between items-center text-xs bg-[#18181B] rounded-lg px-2.5 py-1.5">
                <span className="truncate">{p.termin_label || "Pembayaran"} <span className="text-zinc-600">· {fmtDate(p.date)}</span></span>
                <span className="text-emerald-400 shrink-0 tabular-nums">{rupiah(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentTermins;
